import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Antisaccade — composant jeu principal (paradigme antisaccade web-adapté).
 *
 * Pattern MINIMAL unifié `@labrain/game-shell` (sans LevelSelector ni PedagogieDrawer) :
 *   - `<GameShell>` orchestre xstate + bus + polish + boucle staircase.
 *   - `<PhaseRenderer>` route intro / demo / staircase / wrap_up / debrief.
 *   - `AntisaccadeAdapter` (Promise-based) gère les sub-phases internes
 *     (fixation → cue → gap → target → awaiting_input → feedback → iti).
 *   - `<DebriefShell>` fournit le squelette debrief partagé.
 *
 * Référence paradigm : Hallett 1978, review Munoz & Everling 2004.
 */
import { useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ActionBarProvider, ActionBarSlot, GameShell, PhaseRenderer, useActionBar, useGameShellFromContext, } from '@labrain/game-shell';
import { Demo } from './demo';
import { createAntisaccadeAdapter, makeStimulus, } from './adapter';
import { ASDebriefView, ASStaircaseView } from './staircaseView';
import { paradigmMeta, staircaseConfig } from './params';
const GAME_SLUG = 'antisaccade';
function Intro({ onShowDemo, onSkipDemo }) {
    const { t } = useTranslation(['games', 'common']);
    useActionBar({
        primary: (_jsx("button", { type: "button", onClick: onShowDemo, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.see_demo') })),
        secondary: (_jsx("button", { type: "button", onClick: onSkipDemo, className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.start_direct') })),
    });
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s5 py-s6 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: paradigmMeta.paradigm }), _jsx("h1", { className: "font-display text-[28px] font-medium leading-9 tracking-cy-display text-cyan-text", children: t('antisaccade.title') }), _jsx("p", { className: "max-w-[320px] text-[14.5px] leading-5 text-cyan-text-secondary", children: t('antisaccade.intro_description') }), _jsx(Link, { to: "/library", className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.back_to_play') })] }));
}
function IntroSlot() {
    const shell = useGameShellFromContext();
    return (_jsx(Intro, { onShowDemo: () => shell.startSession(), onSkipDemo: () => shell.skipDemo() }));
}
function DemoSlot() {
    const shell = useGameShellFromContext();
    return (_jsx(Demo, { onComplete: () => shell.markDemoDone(), onSkip: () => shell.markDemoDone() }));
}
function WrapUpSlot() {
    const shell = useGameShellFromContext();
    useMemo(() => {
        try {
            window.localStorage.setItem(`labrain.${paradigmMeta.slug}.lastRun`, JSON.stringify({
                ts: Date.now(),
                threshold: shell.snapshot.staircase.thresholdEstimate,
            }));
        }
        catch {
            /* ignored */
        }
        shell.markWrapUpDone();
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
}
export function AntisaccadeGame() {
    const navigate = useNavigate();
    const adapterRef = useRef(null);
    if (adapterRef.current === null) {
        adapterRef.current = createAntisaccadeAdapter(1);
    }
    const adapter = adapterRef.current;
    const config = useMemo(() => ({
        gameSlug: GAME_SLUG,
        staircaseConfig,
        showDemo: true,
        showCalibrationWarmup: false,
        enableHaptics: true,
        enableSound: true,
        enableVisualFeedback: true,
    }), []);
    return (_jsxs(ActionBarProvider, { children: [_jsx(GameShell, { config: config, adapter: adapter, buildStimulus: (engStim) => makeStimulus(engStim.trialIdx, engStim.value, 1), grader: (_stim, resp) => ({
                    correct: resp.correct,
                    reactionTimeMs: resp.reactionTimeMs,
                    rawInput: { pickedSide: resp.pickedSide, missed: resp.missed },
                }), children: _jsx(PhaseRenderer, { intro: _jsx(IntroSlot, {}), demo: _jsx(DemoSlot, {}), staircase: _jsx(ASStaircaseView, { adapter: adapter, onAbort: () => navigate('/library') }), wrapUp: _jsx(WrapUpSlot, {}), debrief: _jsx(ASDebriefView, {}), aborted: null }) }), _jsx(ActionBarSlot, {})] }));
}
export default AntisaccadeGame;
//# sourceMappingURL=game.js.map